/**
 * PreInstallConfirmationQueue — dispatcher's morning surface.
 *
 * Cockpit-styled: hairline borders, no card chrome, blends seamlessly
 * with the surrounding PlanCockpit surface.
 *
 * Lists every job scheduled in the next 48 hours and shows a 5-point
 * pre-install checklist (customer / materials / permit / balance / access).
 * Sorted: HOLD first, then AT-RISK, then GO. Within group: soonest first.
 *
 * Pure read surface — every "fix" is a deep-link to the job page.
 */
import { forwardRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import {
  buildPreInstallQueue, type ConfirmEntry, type ConfirmStatus,
} from "@/data/scheduling/preInstallConfirmation";
import { cn } from "@/lib/utils";
import { Check, X, Clock, ArrowRight } from "lucide-react";

const STATUS_TONE: Record<ConfirmStatus, { label: string; dot: string; text: string }> = {
  go:      { label: "Go",      dot: "bg-success",     text: "text-success" },
  at_risk: { label: "At risk", dot: "bg-warning",     text: "text-warning" },
  hold:    { label: "Hold",    dot: "bg-destructive", text: "text-destructive" },
};

export function PreInstallConfirmationQueue() {
  const { jobs } = useAppData();
  const entries = useMemo(() => buildPreInstallQueue({ jobs }), [jobs]);

  const counts = {
    hold:   entries.filter(e => e.status === "hold").length,
    atRisk: entries.filter(e => e.status === "at_risk").length,
    go:     entries.filter(e => e.status === "go").length,
  };

  return (
    <section className="border-t border-border/60">
      <header className="px-4 py-3 border-b border-border/60 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
            T-48h confirmation
          </p>
          <p className="text-[11px] text-muted-foreground/80 mt-1">
            {entries.length === 0
              ? "Nothing scheduled in the next 48 hours"
              : `${entries.length} install${entries.length === 1 ? "" : "s"} · sort: HOLD → at risk → go`}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10.5px] tabular-nums">
          <span className={cn("inline-flex items-center gap-1.5", counts.hold > 0 ? "text-destructive font-medium" : "text-muted-foreground/60")}>
            <span className={cn("h-1.5 w-1.5 rounded-full", counts.hold > 0 ? "bg-destructive" : "bg-muted")} />
            {counts.hold} hold
          </span>
          <span className={cn("inline-flex items-center gap-1.5", counts.atRisk > 0 ? "text-warning font-medium" : "text-muted-foreground/60")}>
            <span className={cn("h-1.5 w-1.5 rounded-full", counts.atRisk > 0 ? "bg-warning" : "bg-muted")} />
            {counts.atRisk} at risk
          </span>
          <span className="inline-flex items-center gap-1.5 text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            {counts.go} go
          </span>
        </div>
      </header>

      {entries.length === 0 ? (
        <div className="px-4 py-10 text-center text-[11px] text-muted-foreground">
          Nothing scheduled in the next 48 hours.
        </div>
      ) : (
        <ul>
          {entries.map(e => <Row key={e.job.id} entry={e} />)}
        </ul>
      )}
    </section>
  );
}

const Row = forwardRef<HTMLLIElement, { entry: ConfirmEntry }>(function Row({ entry }, ref) {
  const tone = STATUS_TONE[entry.status];

  return (
    <li ref={ref} className="border-b border-border/40 last:border-0">
      <Link
        to={`/jobs/${entry.job.id}`}
        className="block px-4 py-3 hover:bg-muted/30 transition-colors group"
      >
        <div className="grid grid-cols-[80px_1fr_auto] gap-4 items-center">
          {/* Status — minimal dot + label */}
          <div className={cn("inline-flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-wider", tone.text)}>
            <span className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
            {tone.label}
          </div>

          {/* Job + checks */}
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <p className="text-[12px] font-medium text-foreground truncate">{entry.job.customerName}</p>
              <p className="text-[10.5px] text-muted-foreground truncate">
                {entry.job.projectType} · {entry.job.address.city}
              </p>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {entry.checks.map(c => (
                <span
                  key={c.key}
                  title={`${c.label} — ${c.detail}`}
                  className={cn(
                    "inline-flex items-center gap-1 text-[10px]",
                    c.passed ? "text-success/90" : "text-destructive/90",
                  )}
                >
                  {c.passed
                    ? <Check className="h-2.5 w-2.5" />
                    : <X className="h-2.5 w-2.5" />}
                  {c.label.replace(/^(Customer|Materials|Permit|Balance|Access) /, "")}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground/80 mt-1">{entry.nextAction}</p>
          </div>

          {/* Timing + arrow */}
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1 tabular-nums">
              <Clock className="h-2.5 w-2.5" />
              {Math.max(0, entry.hoursUntilInstall)}h
            </span>
            <ArrowRight className="h-3 w-3 text-muted-foreground/40 group-hover:text-foreground transition-colors" />
          </div>
        </div>
      </Link>
    </li>
  );
});